import React, { useState, useEffect } from 'react';
import {
    Modal, Box, TextField, Button, MenuItem, FormControl,
    InputLabel, Select, Typography, FormHelperText,
    Switch, FormControlLabel, IconButton
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * AttendanceRecordModal Component
 *
 * A reusable modal for editing attendance records (admin form only).
 * Used by both Attendance.js and StudentAttendance.js views.
 *
 * This is a View component that manages its own form state but delegates
 * all business logic (validation, API calls, notifications) to parent components.
 *
 * @param {boolean} open - Whether modal is visible
 * @param {string|ReactNode} title - Modal title
 * @param {boolean} showDateField - Whether to show date field
 * @param {boolean} showDeleteButton - Whether to show delete button
 * @param {object} initialFormData - Initial form state
 * @param {object} errors - Validation errors from parent
 * @param {function} onSave - Callback when Save clicked, receives formData
 * @param {function} onDelete - Callback when Delete clicked
 * @param {function} onClose - Callback when modal should close
 * @param {function} [onReportViolation] - Optional callback for violation button
 * @param {function} onClearField - Callback when field clear button clicked
 */
const AttendanceRecordModal = ({
    open,
    title,
    showDateField = false,
    showDeleteButton = true,
    initialFormData = {},
    errors = {},
    onSave,
    onDelete,
    onClose,
    onReportViolation,
    onClearField
}) => {
    // Internal form state
    const [formData, setFormData] = useState({
        date: '',
        signed_in_at: '',
        signed_out_at: '',
        is_absent: false,
        grant_hours: false,
        granted_hours: 0,
        granted_reason: '',
        granted_details: '',
        ...initialFormData
    });

    // Update internal state when initialFormData changes
    useEffect(() => {
        setFormData({
            date: '',
            signed_in_at: '',
            signed_out_at: '',
            is_absent: false,
            grant_hours: false,
            granted_hours: 0,
            granted_reason: '',
            granted_details: '',
            ...initialFormData
        });
    }, [initialFormData]);

    // Utility: Get current time in HH:MM format
    const currentTime = () => {
        return new Date().toLocaleTimeString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Handle input changes
    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // Handle Absent toggle
    const handleAbsentToggle = () => {
        const newIsAbsent = !formData.is_absent;

        if (newIsAbsent) {
            // Switching to absent - clear sign in/out times, default to personal day
            setFormData({
                ...formData,
                is_absent: true,
                grant_hours: false,
                signed_in_at: '',
                signed_out_at: '',
                granted_hours: 0,
                granted_reason: '',
                granted_details: ''
            });
        } else {
            // Switching to present - set default sign in time
            setFormData({
                ...formData,
                is_absent: false,
                grant_hours: false,
                signed_in_at: currentTime(),
                signed_out_at: '',
                granted_hours: 0,
                granted_reason: '',
                granted_details: ''
            });
        }
    };

    // Handle Grant Hours toggle
    const handleGrantHoursToggle = () => {
        const newGrantHours = !formData.grant_hours;

        if (newGrantHours) {
            // Enable grant hours - use student's default or fallback to 1.0
            const defaultHours = formData.student?.default_granted_hours ||
                                initialFormData.student?.default_granted_hours ||
                                1.0;
            setFormData({
                ...formData,
                grant_hours: true,
                granted_hours: defaultHours,
                granted_reason: '',
                granted_details: ''
            });
        } else {
            // Disable grant hours - clear granted fields
            setFormData({
                ...formData,
                grant_hours: false,
                granted_hours: 0,
                granted_reason: '',
                granted_details: ''
            });
        }
    };

    // Handle field clear button
    const clearField = (fieldName) => {
        setFormData({ ...formData, [fieldName]: '' });
        if (onClearField) {
            onClearField(fieldName);
        }
    };

    // Handle save
    const handleSave = (e) => {
        e.preventDefault();
        if (onSave) {
            onSave(formData);
        }
    };

    // Handle delete
    const handleDelete = () => {
        if (onDelete) {
            onDelete();
        }
    };

    // Handle close
    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    // Handle violation report
    const handleReportViolation = () => {
        if (onReportViolation) {
            onReportViolation();
        }
    };

    // Modal style
    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 2,
    };

    return (
        <Modal
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    handleClose();
                }
            }}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={modalStyle}>
                <Typography variant="h6" sx={{ textAlign: "center" }} id="modal-modal-title">
                    {title}
                </Typography>
                <Box component="form" noValidate sx={{ mt: 1 }} onSubmit={handleSave} id="modal-modal-description">
                    {/* Date Field (optional) */}
                    {showDateField && (
                        <TextField
                            margin="normal"
                            fullWidth
                            name="date"
                            label="Date"
                            type="date"
                            value={formData.date}
                            onChange={handleEditChange}
                            disabled
                        />
                    )}

                    {/* Absent? Switch */}
                    <FormControlLabel
                        sx={{ display: 'block' }}
                        control={
                            <Switch
                                checked={formData.is_absent}
                                onChange={handleAbsentToggle}
                                name="is_absent"
                            />
                        }
                        label="Absent?"
                    />

                    {/* Sign In/Out fields (shown when present) */}
                    {!formData.is_absent && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    name="signed_in_at"
                                    label="Sign In"
                                    type="time"
                                    InputLabelProps={{ shrink: true }}
                                    value={formData.signed_in_at}
                                    onChange={handleEditChange}
                                    error={!!errors.signed_in_at}
                                    helperText={errors.signed_in_at}
                                />
                                <IconButton onClick={() => clearField('signed_in_at')}>
                                    <ClearIcon />
                                </IconButton>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <TextField
                                    margin="normal"
                                    fullWidth
                                    name="signed_out_at"
                                    label="Sign Out"
                                    type="time"
                                    InputLabelProps={{ shrink: true }}
                                    value={formData.signed_out_at}
                                    onChange={handleEditChange}
                                    error={!!errors.signed_out_at}
                                    helperText={errors.signed_out_at}
                                />
                                <IconButton onClick={() => clearField('signed_out_at')}>
                                    <ClearIcon />
                                </IconButton>
                            </Box>
                        </>
                    )}

                    {/* Grant Hours? Switch (always shown) */}
                    <FormControlLabel
                        sx={{ display: 'block' }}
                        control={
                            <Switch
                                checked={formData.grant_hours}
                                onChange={handleGrantHoursToggle}
                                name="grant_hours"
                            />
                        }
                        label="Grant Hours?"
                    />

                    {/* Granted hours fields (shown when grant hours enabled) */}
                    {formData.grant_hours && (
                        <>
                            <TextField
                                margin="normal"
                                fullWidth
                                name="granted_hours"
                                label="Granted Hours"
                                type="number"
                                value={formData.granted_hours}
                                onChange={handleEditChange}
                                error={!!errors.granted_hours}
                                helperText={errors.granted_hours}
                                inputProps={{ step: 0.5, min: 0, max: 24 }}
                            />
                            <FormControl fullWidth margin="normal" error={!!errors.granted_reason}>
                                <InputLabel id="reason-label">Reason</InputLabel>
                                <Select
                                    labelId="reason-label"
                                    name="granted_reason"
                                    value={formData.granted_reason}
                                    label="Reason"
                                    onChange={handleEditChange}
                                >
                                    <MenuItem value="Sick">Sick</MenuItem>
                                    <MenuItem value="Trip">Trip</MenuItem>
                                    <MenuItem value="Work Study">Work Study</MenuItem>
                                    <MenuItem value="Hardship">Hardship</MenuItem>
                                    <MenuItem value="Field Trip">Field Trip</MenuItem>
                                    <MenuItem value="Medical Appointment">Medical Appointment</MenuItem>
                                    <MenuItem value="Weather">Weather</MenuItem>
                                    <MenuItem value="Religious Holiday">Religious Holiday</MenuItem>
                                    <MenuItem value="Suspended">Suspended</MenuItem>
                                    <MenuItem value="Other">Other</MenuItem>
                                </Select>
                                {errors.granted_reason && <FormHelperText>{errors.granted_reason}</FormHelperText>}
                            </FormControl>
                            <TextField
                                margin="normal"
                                fullWidth
                                name="granted_details"
                                label="Details"
                                multiline
                                rows={2}
                                value={formData.granted_details}
                                onChange={handleEditChange}
                            />
                        </>
                    )}

                    {/* Action Buttons */}
                    <Box sx={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
                        <Box>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                sx={{ mr: 1 }}
                            >
                                Save
                            </Button>
                            <Button onClick={handleClose}>
                                Cancel
                            </Button>
                        </Box>
                        <Box>
                            {onReportViolation && (
                                <Button
                                    onClick={handleReportViolation}
                                    style={{ color: '#f44336', marginRight: '8px' }}
                                    variant="text"
                                >
                                    VIOLATION
                                </Button>
                            )}
                            {showDeleteButton && (
                                <Button
                                    onClick={handleDelete}
                                    style={{ color: '#f44336' }}
                                    variant="text"
                                >
                                    Delete
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

export default AttendanceRecordModal;
